import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { PostHogProvider } from '@posthog/react'
import './index.css'
import App from './App.tsx'
import { initializeViewRegistry } from '@/lib/views/init'
import { captureBaseStyles } from '@/lib/print/frameStyles'

// Print frames copy only the app's base CSS (entry + fonts), recorded here
// before any lazy route's CSS loads (src/lib/print/frameStyles.ts).
captureBaseStyles()

// Initialize Sentry error tracking (before any rendering)
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracePropagationTargets: [
    'control.zaviago.com',
    'tenants.zaviago.com',
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
})

// Initialize PostHog product analytics (after Sentry)
const posthogKey = import.meta.env.VITE_POSTHOG_KEY
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: 'https://work.zaviago.com/ingest',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    cross_subdomain_cookie: true,
    enable_recording_console_log: true,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
  })

  posthog.register({ source: 'dashboard' })

  posthog.onSessionId((sessionId) => {
    Sentry.setTag('posthog_session_id', sessionId)
    Sentry.setTag('posthog_session_url',
      `https://us.posthog.com/project/421982/replay/${sessionId}`)
  })
}

// Initialize view registry on app startup
initializeViewRegistry();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    {posthogKey ? (
      <PostHogProvider client={posthog}>
        <App />
      </PostHogProvider>
    ) : (
      <App />
    )}
  </StrictMode>,
)
